import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '../supabase';
import { PrimaryBtn, SecondaryBtn } from './systemKit';
import { classToTone, CONSECUTIVE_RED_THRESHOLD } from '../lib/invoiceDiff';
import { navigate } from '../lib/navigate';
import InvoiceReviewItemDrillIn from './InvoiceReviewItemDrillIn';

// ─── InvoiceReviewModal ──────────────────────────────────────────────────────
//
// Full-screen-ish overlay (centered, ~620px wide) that lets the operator
// dive into a freshly-parsed invoice's price diff. Mounted globally by
// App.js and opened via the `app:open-invoice-review` CustomEvent (see
// lib/navigate.js #openInvoiceReview).
//
// ── Data flow ────────────────────────────────────────────────────────────
// Given just an invoice id, the modal fetches the invoice row + its line
// items (each carrying parse_diff JSON) on mount. Everything else — the
// summary, the sort order, the affected food_item_id list — derives from
// what's already on those rows. No view-time recomputation; FoodPriceTracker
// did it once and we trust the persisted blob.
//
// ── Sort order ───────────────────────────────────────────────────────────
// Flagged rows first (anything not 'flat'), sorted by |delta_pct| desc so
// the biggest mover lands at the top. Flat tail collapsed by default
// behind a "+N steady items" toggle — operator's eye lands on what moved.
//
// ── Action buttons ───────────────────────────────────────────────────────
// Dismiss      → review_status='dismissed'. The operator looked, didn't
//                care, gone. Doesn't reappear in Unreviewed.
// Acknowledge  → review_status='acknowledged'. Same effect on the queue.
//                The split exists for future analytics ("how many invoices
//                does the operator actually engage with vs hide?").
// View affected dishes → URL-param state filter on MenuProfitability,
//                opening the menu page narrowed to dishes that contain
//                any of the affected food_items. Closes the modal first
//                so the operator can see the destination.
//
// ── Close behavior ───────────────────────────────────────────────────────
// ESC key + overlay click both close. Closing without picking an action
// leaves review_status='pending' so the invoice stays in Unreviewed —
// the operator can come back later. This is the explicit "I'm not ready
// to decide" affordance.

const FLAGGED_CLASSES = new Set(['up', 'down', 'pack_changed', 'new_item']);

export default function InvoiceReviewModal({ invoiceId, onClose }) {
  const [invoice, setInvoice] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  // foodItemMeta keyed by food_item_id — { name, category, is_catch_weight }.
  // Only used by the drill-in for header / catch-weight pill, not by the
  // main modal table (which uses raw invoice_line_items names).
  const [foodItemMeta, setFoodItemMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showFlat, setShowFlat] = useState(false);
  // drillItem holds the line_item the operator clicked into for chart
  // drill-in. null when the drill-in is closed. ESC routes here first
  // so a user with the drill-in open can dismiss it without nuking the
  // entire review flow.
  const [drillItem, setDrillItem] = useState(null);

  // Fetch invoice + line items on mount (or when invoiceId changes).
  useEffect(() => {
    let cancelled = false;
    if (!invoiceId) return;
    setLoading(true);
    (async () => {
      const [{ data: inv }, { data: lines }] = await Promise.all([
        supabase.from('invoices').select('*').eq('id', invoiceId).maybeSingle(),
        supabase.from('invoice_line_items').select('*').eq('invoice_id', invoiceId),
      ]);
      if (cancelled) return;
      setInvoice(inv || null);
      setLineItems(lines || []);
      setLoading(false);

      // Follow-up: pull catch-weight + canonical name/category for the
      // food_items referenced by the line items, so the drill-in header
      // can render properly. Single round-trip; lazy — drill-in won't
      // open until this resolves anyway since user has to click a row.
      const ids = Array.from(new Set((lines || []).map(l => l.food_item_id).filter(Boolean)));
      if (ids.length > 0 && inv?.company_id) {
        const { data: fi } = await supabase.from('food_items')
          .select('id, normalized_name, category, is_catch_weight')
          .eq('company_id', inv.company_id)
          .in('id', ids);
        if (cancelled) return;
        const m = {};
        for (const f of (fi || [])) m[f.id] = f;
        setFoodItemMeta(m);
      }
    })();
    return () => { cancelled = true; };
  }, [invoiceId]);

  // ESC key handler. Routes through the drill-in first when it's open
  // so the operator can dismiss the drill-in without dismissing the
  // parent review modal. Capture phase ensures we beat any inner
  // handlers that might intercept — and ensures stopPropagation on the
  // event short-circuits the parent close after we close the drill-in.
  useEffect(() => {
    function onKey(e) {
      if (e.key !== 'Escape') return;
      if (drillItem) {
        e.stopPropagation();
        setDrillItem(null);
        return;
      }
      onClose?.();
    }
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose, drillItem]);

  // Sort: flagged-first by |delta_pct| desc; flat after.
  const { flagged, flat } = useMemo(() => {
    const f = [];
    const s = [];
    for (const li of lineItems) {
      const d = li.parse_diff;
      if (d && FLAGGED_CLASSES.has(d.class)) f.push(li); else s.push(li);
    }
    f.sort((a, b) => {
      const da = Math.abs(a.parse_diff?.delta_pct ?? 0);
      const db = Math.abs(b.parse_diff?.delta_pct ?? 0);
      return db - da;
    });
    return { flagged: f, flat: s };
  }, [lineItems]);

  const summary = invoice?.parse_summary || null;
  const affectedFoodItemIds = useMemo(() => {
    const ids = new Set();
    for (const li of lineItems) {
      const d = li.parse_diff;
      if (d && FLAGGED_CLASSES.has(d.class) && li.food_item_id) {
        ids.add(li.food_item_id);
      }
    }
    return [...ids];
  }, [lineItems]);

  const setReviewStatus = useCallback(async (status) => {
    if (!invoice || submitting) return;
    setSubmitting(true);
    try {
      await supabase.from('invoices').update({ review_status: status }).eq('id', invoice.id);
      onClose?.();
    } catch (e) {
      console.error('[InvoiceReviewModal] failed to set review_status', e);
    } finally {
      setSubmitting(false);
    }
  }, [invoice, submitting, onClose]);

  const handleViewAffected = useCallback(() => {
    if (affectedFoodItemIds.length === 0) return;
    // Acknowledge as a side-effect of "View affected dishes" — the
    // operator did engage with the diff, even if they're navigating away.
    // Fire-and-forget; we don't want navigation to wait on the round-trip.
    if (invoice) {
      supabase.from('invoices')
        .update({ review_status: 'acknowledged' })
        .eq('id', invoice.id)
        .then(() => {});
    }
    navigate('menu', 'profitability', {
      affectedFoodItemIds,
      sourceInvoiceId: invoice?.id,
      sourceVendor: invoice?.vendor_name,
    });
    onClose?.();
  }, [affectedFoodItemIds, invoice, onClose]);

  if (!invoiceId) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Invoice review"
      className="invoice-review-overlay no-print"
      // Per DESIGN_SYSTEM.md §High-stakes review surface close convention,
      // overlay click does NOT dismiss this modal. The dim backdrop is
      // visual cover only. Close routes are: X button, ESC key, or one
      // of the explicit action buttons (Dismiss / Acknowledge / View
      // affected dishes). This is the v2 behavior; v1 (commit 02f6dfc)
      // had overlay-click close, removed after operator pushback that
      // a misclick during deep review destroys the moment.
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(15, 17, 22, 0.55)', zIndex: 70,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        className="invoice-review-content"
        style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          borderRadius: 12,
          width: '100%',
          maxWidth: 620,
          maxHeight: 'calc(100vh - 40px)',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px 24px 16px 24px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 10, fontWeight: 700, letterSpacing: '0.1em',
              textTransform: 'uppercase', color: 'var(--text-secondary)',
              marginBottom: 6,
            }}>Invoice Review</div>
            <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.3 }}>
              {invoice?.vendor_name?.replace(/\n/g, ' / ') || 'Invoice'} · {invoice?.invoice_date || ''}
            </div>
            {invoice?.invoice_number && (
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                #{invoice.invoice_number}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: 22,
              lineHeight: 1,
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              padding: 4,
              borderRadius: 4,
            }}
          >×</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
          {loading && (
            <div style={{ color: 'var(--text-secondary)', fontSize: 13, padding: 16 }}>Loading…</div>
          )}

          {!loading && (
            <>
              {/* Summary stats strip */}
              <SummaryStrip summary={summary} totalLines={lineItems.length} />

              {/* Flagged line items — clickable; click opens the drill-in */}
              {flagged.length > 0 && (
                <div style={{ marginTop: 20 }}>
                  <SectionHeader>What moved</SectionHeader>
                  <LineItemsTable items={flagged} onItemClick={setDrillItem} />
                </div>
              )}

              {/* Flat tail — purely informational, NOT clickable
                  (by design — there's no story to tell on a flat line) */}
              {flat.length > 0 && (
                <div style={{ marginTop: 20 }}>
                  <button
                    onClick={() => setShowFlat(s => !s)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: 'var(--text-secondary)',
                      fontSize: 12,
                      cursor: 'pointer',
                      padding: '4px 0',
                    }}
                  >
                    {showFlat ? '▾' : '▸'} {flat.length} steady items
                  </button>
                  {showFlat && <LineItemsTable items={flat} muted />}
                </div>
              )}

              {flagged.length === 0 && flat.length > 0 && (
                <div style={{
                  marginTop: 16,
                  padding: 12,
                  background: 'var(--bg-subtle)',
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                  fontSize: 13,
                  color: 'var(--text-secondary)',
                }}>
                  All prices steady on this invoice.
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer actions */}
        <div style={{
          padding: '14px 24px',
          borderTop: '1px solid var(--border)',
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          justifyContent: 'flex-end',
          background: 'var(--bg-elevated)',
        }}>
          <SecondaryBtn onClick={() => setReviewStatus('dismissed')} disabled={submitting}>
            Dismiss
          </SecondaryBtn>
          {affectedFoodItemIds.length > 0 && (
            <SecondaryBtn onClick={handleViewAffected} disabled={submitting}>
              View affected dishes
            </SecondaryBtn>
          )}
          <PrimaryBtn onClick={() => setReviewStatus('acknowledged')} disabled={submitting}>
            {submitting ? 'Saving…' : 'Acknowledge'}
          </PrimaryBtn>
        </div>
      </div>

      {/* Per-line drill-in. Mounted inside the modal's overlay so it
          stacks correctly above the modal content (z=80 vs modal z=70).
          When the operator closes the parent modal via Acknowledge /
          Dismiss / View affected dishes, this entire subtree unmounts —
          no stranded drill-in left behind.

          Catch-weight + canonical name come from foodItemMeta (fetched
          in the modal's mount effect); fall back to the line's own
          fields if a food_item lookup is missing for any reason. */}
      <InvoiceReviewItemDrillIn
        open={!!drillItem}
        onClose={() => setDrillItem(null)}
        companyId={invoice?.company_id}
        foodItemId={drillItem?.food_item_id}
        itemName={
          (drillItem?.food_item_id && foodItemMeta[drillItem.food_item_id]?.normalized_name)
          || drillItem?.normalized_name
          || drillItem?.raw_description
          || 'Item'
        }
        itemCategory={
          (drillItem?.food_item_id && foodItemMeta[drillItem.food_item_id]?.category)
          || drillItem?.category
          || null
        }
        isCatchWeight={
          drillItem?.food_item_id ? !!foodItemMeta[drillItem.food_item_id]?.is_catch_weight : false
        }
        currentInvoiceId={invoice?.id}
        currentInvoiceDate={invoice?.invoice_date}
        parseDiff={drillItem?.parse_diff || null}
      />
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
function SectionHeader({ children }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 600, letterSpacing: '0.08em',
      textTransform: 'uppercase', color: 'var(--text-secondary)',
      marginBottom: 8,
    }}>{children}</div>
  );
}

function SummaryStrip({ summary, totalLines }) {
  if (!summary) {
    return (
      <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
        {totalLines} line items.
      </div>
    );
  }
  const moved = summary.moved_count || 0;
  const cost = summary.total_cost_change;
  const costStr = cost == null ? '—'
    : cost === 0 ? '$0'
    : `${cost > 0 ? '+' : '−'}$${Math.abs(cost).toFixed(2)}`;
  const costTone = cost > 0 ? 'var(--text-warning)'
    : cost < 0 ? 'var(--text-success)'
    : 'var(--text-secondary)';

  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12,
      padding: 12, background: 'var(--bg-subtle)', borderRadius: 8,
      border: '1px solid var(--border)',
    }}>
      <Stat label="Items moved" value={`${moved} / ${summary.total_lines || totalLines}`} />
      <Stat label="Cost change" value={costStr} valueColor={costTone} />
      <Stat
        label="Flags"
        value={summary.has_red_flag ? 'Trend (3+)' : (summary.pack_changed > 0 ? 'Pack' : (moved > 0 ? 'Movers' : '—'))}
        valueColor={summary.has_red_flag ? 'var(--text-critical)' : 'var(--text-primary)'}
      />
    </div>
  );
}

function Stat({ label, value, valueColor }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
                    textTransform: 'uppercase', color: 'var(--text-secondary)',
                    marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 16, fontWeight: 600, color: valueColor || 'var(--text-primary)' }}>
        {value}
      </div>
    </div>
  );
}

function LineItemsTable({ items, muted = false, onItemClick = null }) {
  return (
    <div style={{
      border: '1px solid var(--border)',
      borderRadius: 8,
      overflow: 'hidden',
      background: 'var(--bg-card)',
    }}>
      {items.map((li, i) => (
        <LineItemRow
          key={li.id}
          line={li}
          muted={muted}
          isFirst={i === 0}
          // Only make the row clickable when we have a food_item_id to
          // drill into. Lines that failed upstream matching (no
          // food_item_id) have no comparable trend, so the drill-in
          // would just show "Loading…" forever.
          onClick={onItemClick && li.food_item_id ? () => onItemClick(li) : null}
        />
      ))}
    </div>
  );
}

function LineItemRow({ line, muted, isFirst, onClick = null }) {
  const d = line.parse_diff || {};
  const consecutive = d.consecutive_increases || 0;
  const tone = classToTone(d.class, consecutive);
  const isFlagged = FLAGGED_CLASSES.has(d.class);
  const clickable = !!onClick;

  // Subtle tint on flagged rows. Tone-aware so red lines feel meaningfully
  // hotter than amber/green.
  const rowBg = !isFlagged ? 'transparent'
    : tone === 'red' ? 'color-mix(in srgb, var(--text-critical) 6%, transparent)'
    : tone === 'amber' ? '#FAEEDA22'
    : tone === 'green' ? 'color-mix(in srgb, var(--text-success) 5%, transparent)'
    : 'transparent';

  return (
    <div
      onClick={clickable ? onClick : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
      onMouseEnter={clickable ? (e) => { e.currentTarget.style.background = 'var(--bg-hover)'; } : undefined}
      onMouseLeave={clickable ? (e) => { e.currentTarget.style.background = rowBg; } : undefined}
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto auto',
        gap: 12,
        padding: '10px 12px',
        borderTop: isFirst ? 'none' : '1px solid var(--border)',
        background: rowBg,
        opacity: muted ? 0.7 : 1,
        alignItems: 'center',
        cursor: clickable ? 'pointer' : 'default',
        transition: 'background 120ms ease',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{
          fontSize: 13, fontWeight: 500, color: 'var(--text-primary)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {line.normalized_name || line.raw_description || '(unnamed)'}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
          {line.product_code ? `#${line.product_code} · ` : ''}{line.vendor_name?.replace(/\n/g, ' / ')}
        </div>
      </div>

      <div style={{ textAlign: 'right' }}>
        <div style={{ fontSize: 13, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>
          {fmtPrice(d.current_price)}
        </div>
        {d.baseline_price != null && (
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
            was {fmtPrice(d.baseline_price)}
          </div>
        )}
      </div>

      <DiffPill diff={d} tone={tone} />
    </div>
  );
}

function DiffPill({ diff, tone }) {
  const klass = diff?.class;
  if (!klass) return null;
  const colors = pillColors(tone);

  // pack_changed gets the killer-feature visual: "50# → 45#"
  if (klass === 'pack_changed' && diff.pack_changed) {
    return (
      <span style={{
        display: 'inline-block',
        padding: '4px 8px',
        borderRadius: 6,
        fontSize: 11,
        fontWeight: 600,
        background: colors.bg,
        color: colors.fg,
        border: `1px solid ${colors.border}`,
        whiteSpace: 'nowrap',
        fontVariantNumeric: 'tabular-nums',
      }}>
        {diff.pack_changed.from} → {diff.pack_changed.to}
      </span>
    );
  }

  if (klass === 'new_item') {
    return (
      <span style={{
        display: 'inline-block', padding: '4px 8px', borderRadius: 6,
        fontSize: 11, fontWeight: 600,
        background: colors.bg, color: colors.fg,
        border: `1px solid ${colors.border}`,
      }}>NEW</span>
    );
  }

  if (klass === 'flat') {
    return (
      <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>—</span>
    );
  }

  // up / down: signed percent
  const pct = diff.delta_pct;
  if (pct == null) return null;
  const label = `${pct > 0 ? '+' : ''}${(pct * 100).toFixed(1)}%`;
  const showStreak = klass === 'up' && diff.consecutive_increases >= CONSECUTIVE_RED_THRESHOLD;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '4px 8px', borderRadius: 6,
      fontSize: 11, fontWeight: 600,
      background: colors.bg, color: colors.fg,
      border: `1px solid ${colors.border}`,
      whiteSpace: 'nowrap',
      fontVariantNumeric: 'tabular-nums',
    }}>
      {label}
      {showStreak && (
        <span style={{ fontSize: 10, opacity: 0.85 }}>
          ({diff.consecutive_increases}×)
        </span>
      )}
    </span>
  );
}

function pillColors(tone) {
  switch (tone) {
    case 'red':
      return {
        bg: 'color-mix(in srgb, var(--text-critical) 12%, transparent)',
        fg: 'var(--text-critical)',
        border: 'color-mix(in srgb, var(--text-critical) 35%, transparent)',
      };
    case 'amber':
      return {
        bg: 'color-mix(in srgb, var(--text-warning) 14%, transparent)',
        fg: 'var(--text-warning)',
        border: 'color-mix(in srgb, var(--text-warning) 40%, transparent)',
      };
    case 'green':
      return {
        bg: 'color-mix(in srgb, var(--text-success) 12%, transparent)',
        fg: 'var(--text-success)',
        border: 'color-mix(in srgb, var(--text-success) 35%, transparent)',
      };
    case 'neutral':
    default:
      return {
        bg: 'var(--bg-subtle)',
        fg: 'var(--text-secondary)',
        border: 'var(--border)',
      };
  }
}

function fmtPrice(p) {
  if (p == null) return '—';
  const n = Number(p);
  if (!Number.isFinite(n)) return '—';
  return `$${n.toFixed(2)}`;
}
